import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect, Option } from "@/components/ui/multi-select";
import { useRoles, useAssignRole } from "@/lib/api/roles";
import { useGroups } from "@/lib/api/group";
import { useAbility } from "@/lib/casl/ability-context";

interface AssignRoleDialogProps {
  userId: string;
  existingRoleIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssignRoleDialog({
  userId,
  existingRoleIds,
  open,
  onOpenChange,
}: AssignRoleDialogProps) {
  const { data: roles } = useRoles();
  const { data: groups } = useGroups(false);
  const assignRole = useAssignRole(userId);
  const ability = useAbility();
  const canManageAll = ability.can("manage", "all");

  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const groupOptions = useMemo<Option[]>(
    () =>
      groups?.map((group) => ({
        value: group.id,
        label: group.name,
      })) ?? [],
    [groups],
  );

  const selectedGroups = useMemo(
    () =>
      selectedGroupIds.map(
        (groupId) =>
          groupOptions.find((group) => group.value === groupId) ?? {
            value: groupId,
            label: groupId,
          },
      ),
    [groupOptions, selectedGroupIds],
  );

  const availableRoles = roles?.filter(
    (r) =>
      !existingRoleIds.includes(r.id) &&
      (canManageAll || r.name !== "superadmin"),
  );

  const handleSubmit = () => {
    if (!selectedRoleId) return;

    const scope =
      selectedGroupIds.length > 0 ? { groups: selectedGroupIds } : undefined;

    assignRole.mutate(
      { roleId: selectedRoleId, scope },
      {
        onSuccess: () => {
          setSelectedRoleId("");
          setSelectedGroupIds([]);
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Role</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role..." />
              </SelectTrigger>
              <SelectContent>
                {availableRoles?.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                    {role.is_system && " (system)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Group Scope (optional)</Label>
            <p className="text-xs text-muted-foreground">
              {canManageAll
                ? "Limit this role to specific groups. Leave empty for unscoped access."
                : "Select at least one group to scope this role."}
            </p>
            <MultiSelect
              options={groupOptions}
              value={selectedGroups}
              onChange={(selected) =>
                setSelectedGroupIds(selected.map((option) => option.value))
              }
              placeholder="Select groups..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !selectedRoleId ||
              assignRole.isPending ||
              (!canManageAll && selectedGroupIds.length === 0)
            }
          >
            {assignRole.isPending ? "Assigning..." : "Assign Role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
